import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchOcdAssessment } from "../../../store/slices/ocdAssessmentsSlice";
import logo from "../../../assets/images/logo-samplee.png";

export default function ObsessiveCompulsiveTestFormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { currentOcdAssessment, isLoading, error } = useSelector((state) => state.ocdAssessments || {});

    useEffect(() => {
        if (id) dispatch(fetchOcdAssessment(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentOcdAssessment) {
            const timer = setTimeout(() => window.print(), 1000);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentOcdAssessment, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading Y-BOCS Report...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentOcdAssessment) return <div className="p-8 text-center">No record found.</div>;

    const d = currentOcdAssessment;

    // Direct mapping to your backend snake_case response keys
    const ocdItems = [
        { title: "Time spent on Obsessions", scoreKey: "obsessions_time_score", valueKey: "obsessions_time_value" },
        { title: "Interference from Obsessions", scoreKey: "obsessions_interference_score", valueKey: "obsessions_interference_value" },
        { title: "Distress from Obsessions", scoreKey: "obsessions_distress_score", valueKey: "obsessions_distress_value" },
        { title: "Resistance against Obsessions", scoreKey: "obsessions_resistance_score", valueKey: "obsessions_resistance_value" },
        { title: "Control over Obsessions", scoreKey: "obsessions_control_score", valueKey: "obsessions_control_value" },
        { title: "Time spent on Compulsions", scoreKey: "compulsions_time_score", valueKey: "compulsions_time_value" },
        { title: "Interference from Compulsions", scoreKey: "compulsions_interference_score", valueKey: "compulsions_interference_value" },
        { title: "Anxiety if prevented (Compulsions)", scoreKey: "compulsions_anxiety_score", valueKey: "compulsions_anxiety_value" },
        { title: "Resistance against Compulsions", scoreKey: "compulsions_resistance_score", valueKey: "compulsions_resistance_value" },
        { title: "Control over Compulsions", scoreKey: "compulsions_control_score", valueKey: "compulsions_control_value" }
    ];

    const totalScore = ocdItems.reduce((sum, item) => sum + (parseInt(d[item.scoreKey] || 0, 10)), 0);

    const getSeverity = (score) => {
        if (score <= 7) return "Subclinical";
        if (score <= 15) return "Mild OCD";
        if (score <= 23) return "Moderate OCD";
        if (score <= 31) return "Severe OCD";
        return "Extreme OCD";
    };

    return (
        <>
            <style jsx global>{`
                * { margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; font-family: 'Segoe UI', Arial, sans-serif !important; }
                body { background: #fff !important; color: #000 !important; font-size: 12px !important; }
                .print-container { padding: 30px !important; position: relative !important; }
                .logo { position: absolute !important; top: 15px !important; left: 15px !important; }
                .logo img { height: 60px !important; width: auto !important; }
                .header { text-align: center !important; margin-bottom: 20px !important; border-bottom: 2.5px solid #000 !important; padding-bottom: 10px !important; }
                .header h3 { font-size: 1.5rem !important; font-weight: bold !important; }
                .meta-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 20px !important; }
                .meta-table td { border: 1px solid #000 !important; padding: 8px !important; }
                .label-bg { background: #f2f2f2 !important; font-weight: bold !important; width: 18% !important; }
                .ocd-table { width: 100% !important; border-collapse: collapse !important; }
                .ocd-table th, .ocd-table td { border: 1px solid #000 !important; padding: 8px 10px !important; }
                .ocd-table th { background: #eee !important; font-weight: bold !important; text-align: center !important; }
                .score-col { width: 60px !important; text-align: center !important; font-weight: bold !important; font-size: 14px !important; }
                .category-title { font-weight: bold !important; background: #fafafa !important; width: 180px !important; }
                .result-summary { margin-top: 25px !important; border: 2px solid #000 !important; display: flex !important; page-break-inside: avoid !important; }
                .result-score { background: #000 !important; color: #fff !important; padding: 15px 30px !important; text-align: center !important; min-width: 130px !important; }
                .result-score h2 { font-size: 2.2rem !important; margin: 0 !important; }
                .result-text { flex-grow: 1 !important; padding: 15px !important; text-align: center !important; display: flex !important; flex-direction: column !important; justify-content: center !important; }
                .severity-label { font-size: 1.4rem !important; font-weight: bold !important; text-decoration: underline !important; text-transform: uppercase !important; }
                .footer { margin-top: 35px !important; border-top: 1px solid #000 !important; padding-top: 10px !important; text-align: center !important; font-size: 10px !important; }
                .sig-row { display: flex !important; justify-content: space-between !important; margin-top: 50px !important; }
                .sig-box { border-top: 1px solid #000 !important; width: 220px !important; text-align: center !important; padding-top: 5px !important; font-weight: bold !important; }
                @media print { .ocd-table tr { page-break-inside: avoid !important; } }
            `}</style>

            <div className="print-container">
                <div className="logo"><img src={logo} alt="Logo" /></div>
                <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>YALE-BROWN OBSESSIVE COMPULSIVE SCALE (Y-BOCS)</h6>
                </div>

                <table className="meta-table">
                    <tbody>
                        <tr>
                            <td className="label-bg">Client Name</td>
                            <td>{d.client_name}</td>
                            <td className="label-bg">Date</td>
                            <td>{new Date(d.created_at).toLocaleDateString('en-GB')}</td>
                        </tr>
                        <tr>
                            <td className="label-bg">Diagnosis</td>
                            <td>{d.diagnosis}</td>
                            <td className="label-bg">Assessed By</td>
                            <td>{d.assessed_by}</td>
                        </tr>
                    </tbody>
                </table>

                <table className="ocd-table">
                    <thead>
                        <tr>
                            <th style={{ width: '35px' }}>#</th>
                            <th className="category-title">Scale Item</th>
                            <th>Patient Response (Observations)</th>
                            <th className="score-col">Score</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td colSpan="4" style={{ background: '#f8f9fa', fontWeight: 'bold', fontSize: '10px' }}>PART I: OBSESSIONS</td>
                        </tr>
                        {ocdItems.slice(0, 5).map((item, index) => (
                            <tr key={index}>
                                <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                <td className="category-title">{item.title}</td>
                                <td>{d[item.valueKey]}</td>
                                <td className="score-col">{d[item.scoreKey]}</td>
                            </tr>
                        ))}
                        <tr>
                            <td colSpan="4" style={{ background: '#f8f9fa', fontWeight: 'bold', fontSize: '10px' }}>PART II: COMPULSIONS</td>
                        </tr>
                        {ocdItems.slice(5).map((item, index) => (
                            <tr key={index + 5}>
                                <td style={{ textAlign: 'center' }}>{index + 6}</td>
                                <td className="category-title">{item.title}</td>
                                <td>{d[item.valueKey]}</td>
                                <td className="score-col">{d[item.scoreKey]}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* <div className="result-summary">
                    <div className="result-score">
                        <small style={{ fontSize: '10px' }}>YBOCS TOTAL SCORE</small>
                        <h2>{totalScore}</h2>
                    </div>
                    <div className="result-text">
                        <small style={{ fontSize: '10px' }}>CLINICAL SEVERITY INDICATOR</small>
                        <div className="severity-label">{getSeverity(totalScore)}</div>
                    </div>
                </div> */}

                <div className="sig-row">
                    <div className="sig-box">Patient Signature</div>
                    <div className="sig-box">Assessing Practitioner Signature</div>
                </div>

                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
        </>
    );
}